import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

/**
 * Globální uživatelské preference (persist do localStorage).
 *
 * - vatMode: jak zobrazovat cenu — bez DPH, s DPH, nebo obě hodnoty
 * - currency: primární měna (druhá se zobrazuje jako doplněk)
 */

export type VatMode = "net" | "gross" | "both";
export type CurrencyMode = "CZK" | "EUR";
export type LocaleMode = "cs" | "sk" | "en";

interface Settings {
  vatMode: VatMode;
  currency: CurrencyMode;
  locale: LocaleMode;
}

interface SettingsCtx extends Settings {
  setVatMode: (m: VatMode) => void;
  setCurrency: (c: CurrencyMode) => void;
  setLocale: (l: LocaleMode) => void;
}

const STORAGE_KEY = "ktm.settings";
const DEFAULT: Settings = { vatMode: "both", currency: "CZK", locale: "cs" };

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      vatMode: parsed.vatMode ?? DEFAULT.vatMode,
      currency: parsed.currency ?? DEFAULT.currency,
      locale: parsed.locale ?? DEFAULT.locale,
    };
  } catch {
    return DEFAULT;
  }
}

const Ctx = createContext<SettingsCtx | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [settings, setSettings] = useState<Settings>(() => load());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const value: SettingsCtx = {
    ...settings,
    setVatMode: (vatMode) => setSettings((s) => ({ ...s, vatMode })),
    setCurrency: (currency) => setSettings((s) => ({ ...s, currency })),
    setLocale: (locale) => setSettings((s) => ({ ...s, locale })),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSettings(): SettingsCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSettings must be used inside <SettingsProvider>");
  return ctx;
}
